import React, { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import Loader from "../common/Loader";

type ModuleName =
  | "tenant"
  | "user"
  | "users"
  | "clients"
  | "playlist"
  | "playlists"
  | "roles"
  | "permissions"
  | "screens"
  | "creatives"
  | "settings"
  | "client-playlists"
  | "control-panel"
  | "schedular"
  | string;

interface ProtectedRouteProps {
  children: React.ReactNode;
  route?: ModuleName | ModuleName[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, route }) => {
  const { isAuthenticated, isLoading, profile } = useAuth();
  const location = useLocation();

  const normalizedPermissions = useMemo(() => {
    if (!profile) return {};
    const permissionsArray: string[] = Array.isArray(profile.permissions)
      ? profile.permissions
      : [];
    return permissionsArray.reduce(
      (acc: Record<string, string[]>, permission: string) => {
        const [module, action] = permission.split(".");
        if (module && action) {
          acc[module] = acc[module] || [];
          acc[module].push(action);
        }
        return acc;
      },
      {},
    );
  }, [profile]);

  if (isLoading) {
    return <Loader fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (route) {
    if (!Array.isArray(profile?.permissions)) {
      return <Loader fullScreen />;
    }

    // Support both single module and array of modules
    const modulesToCheck = Array.isArray(route) ? route : [route];
    const hasAnyPermission = modulesToCheck.some((module) => {
      const routePermissions = normalizedPermissions[module] ?? [];
      return routePermissions.length > 0;
    });

    if (!hasAnyPermission) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
